import { useState, useMemo, useEffect } from "react"
import { ReactComponent as ArrowDownImg } from "../assets/arrow-down.svg";
import { ReactComponent as AcceptImg } from "../assets/accept.svg";
import { ReactComponent as CheeckMarkImg } from "../assets/checkmark-circle-outline.svg";

export default function PollVoting({ children: poll, onSubmit, onToggle }) {
    const canVote = poll.active && !poll.choice;

    const normalizedResults = useMemo(() => {
        if (!poll.results) return null;

        const sum = poll.results.reduce((p, c) => p + c, 0);
        if (sum === 0) return poll.results.map((r) => 0);

        return poll.results.map((r) => (r / sum))
    }, [poll.results])
    const maxResultPercent = Math.max.apply(null, normalizedResults)

    // expand
    const [isExpanded, setExpanded] = useState(false);
    useEffect(() => {
        if (typeof onToggle === "function") {
            onToggle();
        }
    }, [isExpanded, onToggle]);

    // voting
    const [selectedChoice, setSelectedChoice] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const renderItem = (answer, idx) => {
        const selected = (poll.choice != null && poll.choice.indexOf(idx) > -1) || selectedChoice.indexOf(idx) > -1;
        const percentage = poll.results && `${Math.round((normalizedResults[idx] === 0 ? 0 : (normalizedResults[idx] / maxResultPercent)) * 100)}%`;

        return <button
            key={idx}
            disabled={!canVote || isSubmitting}
            onClick={canVote ? () => doAnswer(idx) : null}
            className={`w-full border border-on-primary relative rounded-md transition-colors ${canVote ? (selected ? "bg-on-primary" : "hover:bg-on-primary group") : "cursor-default"}`}>

            <div className={`flex text-left px-2 py-1.5 ${canVote ? (selected ? "text-primary" : "text-on-primary group-hover:text-primary") : "text-on-primary"}`}>
                <span className="flex-1">{selected && <AcceptImg className="inline-block w-4 h-4" />} {answer}</span>
                {percentage && <b>{Math.round(normalizedResults[idx] * 100)}%</b>}
            </div>

            {percentage && <div key={`result-${idx}-${percentage}`} className={`absolute left-0 top-0 h-full w-full rounded animate-clip ${normalizedResults[idx] > 0 ? "bg-on-primary" : ""}`} style={{ clipPath: `polygon(0 0, ${percentage} 0, ${percentage} 100%, 0 100%)` }}>
                <div className={`flex text-left px-2 py-1.5 space-x-3 ${normalizedResults[idx] > 0 || selected ? "text-primary" : ""} group-hover:text-primary`}>
                    <span className="flex-1">{selected && <AcceptImg className="inline-block w-4 h-4" />} {answer}</span>
                    <b>{Math.round(normalizedResults[idx] * 100)}%</b>
                </div>
            </div>}
        </button>;
    }

    const doAnswer = (idx) => {
        if (poll.details.type === "single") {
            submit([idx]);
        } else {
            setSelectedChoice((current) => {
                if (current.indexOf(idx) > -1) {
                    return current.filter((c) => c !== idx);
                } else {
                    return [...current, idx];
                }
            })
        }
    }

    const submit = (choice) => {
        setIsSubmitting(true);
        onSubmit(choice).catch((error) => {
            console.error(error);
            setIsSubmitting(false);
        });
    }

    return (
        <div className="bg-primary animate-bg-pulse text-on-primary text-sm px-3 select-none rounded">
            <div className="flex space-x-2 py-3 min-h-12 justify-between items-center cursor-pointer" onClick={() => setExpanded(!isExpanded)}>
                <span className="flex-1 text-on-primary mr-1">
                    {<CheeckMarkImg className={`w-4 h-4 inline-block mr-1 self-start mt-0.5 ${canVote ? "animate-bounce" : ""}`} />}
                    {poll.details.question}
                </span>

                <ArrowDownImg className={`transform transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`} />
            </div>

            {isExpanded && <div className="space-y-1 pb-3">
                {poll.details.answers.map(renderItem)}
                {canVote && poll.details.type === "multi" && <>
                    <div className="mt-1"></div>
                    <button disabled={isSubmitting || selectedChoice.length === 0} className="font-bold w-full text-center disabled:opacity-60 disabled:cursor-default" onClick={() => submit(selectedChoice)}>Submit your answer</button>
                </>}
            </div>}
        </div>
    );
}
